import { ITEMS } from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { loadAssetManifest, resolveAssetUrl } from '../../core/assetManifest.js';
import { itemTextureKey } from '../../avatar/parseOutfit.js';
import {
  KNOWN_ITEM_SPRITE_FILES,
  resolvedSpriteFileId,
} from '../../avatar/itemVisual.js';
import { ensureItemTextures } from '../../avatar/proceduralItemTexture.js';
import { loadProfileDraft } from '../onboarding/authClient.js';
import { SplashScene } from './SplashScene.js';

export class PreloadScene extends BaseScene {
  static readonly KEY = 'Preload';

  constructor() {
    super(PreloadScene.KEY);
  }

  preload(): void {
    // placeholdery boot — zawsze dostępne
    this.load.image('ph_logo', '/assets/placeholder/logo.png');
    this.load.image('ph_dog', '/assets/placeholder/dog.png');
    // WP13: sprite Missi (maltańczyk) jako baza awatara maltese — fallback proceduralny zostaje
    // Pełny zestaw klatek animacji (idle + cykl chodu + akcje) — bazowy awatar chodzącego pieska.
    this.load.image('dog_maltese_idle', '/assets/real/maltese_idle.png');
    this.load.image('dog_maltese_walkA', '/assets/real/maltese_walkA.png');
    this.load.image('dog_maltese_walkB', '/assets/real/maltese_walkB.png');
    this.load.image('dog_maltese_sit', '/assets/real/maltese_sit.png');
    this.load.image('dog_maltese_jump', '/assets/real/maltese_jump.png');
    this.load.image('dog_maltese_wave', '/assets/real/maltese_wave.png');
    this.load.image('dog_maltese_eat', '/assets/real/dog_maltese_eat.png');
    this.load.image('dog_maltese_sleep', '/assets/real/dog_maltese_sleep.png');
    this.load.image('cat_idle', '/assets/real/cat_idle.png');
    this.load.image('cat_walkA', '/assets/real/cat_walkA.png');
    this.load.image('cat_walkB', '/assets/real/cat_walkB.png');
    this.load.image('bg_icecream', '/assets/real/bg_icecream.png');
    this.load.image('icecream_cone', '/assets/real/icecream_cone_empty.png');
    this.load.image('icecream_cup_empty', '/assets/real/icecream_cup_empty.png');
    for (const flavor of ['strawberry', 'chocolate', 'vanilla', 'mint']) {
      this.load.image(
        `icecream_flavor_${flavor}`,
        `/assets/real/icecream_flavor_${flavor}.png`,
      );
    }
    // Portrety 6 ras (kreator pieska) + idle awatara dla ras poza maltese
    for (const b of ['maltese', 'corgi', 'poodle', 'husky', 'dachshund', 'golden']) {
      this.load.image(`breed_${b}`, `/assets/real/breed_${b}.png`);
    }
    for (const b of ['corgi', 'poodle', 'husky', 'dachshund', 'golden']) {
      this.load.image(`dog_${b}_idle`, `/assets/real/dog_${b}_idle.png`);
    }
    // Obiekty interaktywne + ubranka (sprite'y Higgsfield; brak → fallback proceduralny w kodzie).
    // Konwencja: klucz tekstury == nazwa pliku w /assets/real/.
    for (const o of ['ball', 'bench', 'bird', 'cat', 'flowerbox', 'fountain', 'lamp', 'puddle']) {
      this.load.image(`obj_town_${o}`, `/assets/real/obj_town_${o}.png`);
    }
    // Ubranka — klucz == item_<id> (parseOutfit.itemTextureKey).
    // 1) PNG z /assets/real (w tym aliasy id→plik), 2) reszta: ensureItemTextures w create.
    const loadedKeys = new Set<string>();
    for (const item of ITEMS) {
      const fileId = resolvedSpriteFileId(item.id);
      if (!fileId) continue;
      const key = itemTextureKey(item.id);
      if (loadedKeys.has(key)) continue;
      this.load.image(key, `/assets/real/item_${fileId}.png`);
      loadedKeys.add(key);
    }
    // pliki generic (item_hat.png itd.) — tylko gdy nie nadpisały id katalogu
    for (const fileId of KNOWN_ITEM_SPRITE_FILES) {
      const key = `item_${fileId}`;
      if (loadedKeys.has(key)) continue;
      // nie ładuj pod kluczem pliku jeśli to nie jest id z katalogu (unikaj śmieci)
      // — generic item_hat/bow mogą posłużyć jako zapas; ładujemy ostrożnie
      if (ITEMS.some((i) => i.id === fileId)) {
        this.load.image(key, `/assets/real/item_${fileId}.png`);
        loadedKeys.add(key);
      }
    }
  }

  async create(): Promise<void> {
    // rasa wybranego pieska → dostępna dla awatara we wszystkich scenach
    const draft = loadProfileDraft();
    if (draft?.breed) this.registry.set('dogBreed', draft.breed);

    // Proceduralne tekstury dla ubranek bez PNG — widoczne na piesku i wieszakach
    try {
      ensureItemTextures(
        this,
        ITEMS.map((i) => i.id),
      );
    } catch {
      /* headless / brak WebGL — DogAvatar i tak ma awaryjny kształt */
    }

    const cx = this.gameW / 2;
    const cy = this.gameH / 2;
    this.cameras.main.setBackgroundColor(0xffe4f0);
    const label = this.add
      .text(cx, cy, 'Ładowanie…', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
      })
      .setOrigin(0.5);

    const manifest = await loadAssetManifest();
    const boot = manifest.assets.filter((a) => a.group === 'boot');
    for (const entry of boot) {
      const url = resolveAssetUrl(entry);
      // Zachowaj źródło: po dekodowaniu Phaser może zamienić URL na data:,
      // a warstwy UI nadal muszą odróżnić realny asset od placeholdera.
      this.registry.set(`assetSource:${entry.id}`, url);
      if (entry.type === 'image' || entry.type === 'atlas') {
        if (!this.textures.exists(entry.id)) {
          this.load.image(entry.id, url);
        }
      }
    }
    if (boot.length > 0) {
      await new Promise<void>((resolve) => {
        this.load.once('complete', () => resolve());
        this.load.start();
      });
    }

    label.setText('Gotowe!');
    this.time.delayedCall(200, () => {
      this.scene.start(SplashScene.KEY);
    });
  }
}
